import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import API_BASE from "../config";

// ── CreateProjectPage ──────────────────────────────────────────────────
// Two-step new-project flow (PRD addendum 2026-05-20). This page collects
// the METADATA + chooses the location on a satellite map. After saving,
// the user is sent to /projects/:id/sketch to draw field + masts.
//
// Re-used as /projects/:id/edit too — when an :id is in the URL we load
// the existing project and PATCH on save instead of POST.
//
// Mirrors Philip's mockup: form on the left, map preview on the right
// with the address pin. Address autocomplete is Nominatim (same source
// the wizard used to use in Sidebar.js).
// ───────────────────────────────────────────────────────────────────────

const PROJECT_TYPE_OPTIONS = [
  { value: "",          label: "—" },
  { value: "football",  label: "Fußball" },
  { value: "tennis",    label: "Tennis" },
  { value: "basketball",label: "Basketball" },
  { value: "multi",     label: "Mehrzweck" },
  { value: "athletics", label: "Athletik" },
];

const CreateProjectPage = () => {
  const { id: editId } = useParams();    // present when /projects/:id/edit
  const isEdit = Boolean(editId);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    project_name: "",
    priority: "normal",
    project_number: "",
    country: "",
    project_address: "",
    project_town: "",
    project_type: "",
    auto_luminaire_sweep: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ── Address autocomplete (Nominatim, debounced 300ms) ────────────────
  const [addressQuery, setAddressQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searchTimer, setSearchTimer] = useState(null);
  // Selected lon/lat drives the map pin. PLZ-only queries are allowed —
  // Nominatim resolves "30161" to a place in Hannover, for instance.
  const [pin, setPin] = useState(null); // { lon, lat }

  // ── Map (maplibre-gl, satellite tiles, single marker) ────────────────
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Init map once
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    mapRef.current = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          imagery: {
            type: "raster",
            tiles: [
              "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            ],
            tileSize: 256,
          },
        },
        layers: [{ id: "imagery", type: "raster", source: "imagery" }],
      },
      center: [10.4515, 51.1657],   // Germany centroid as fallback
      zoom: 5,
      maxZoom: 19,
      attributionControl: false,
    });
    return () => { mapRef.current?.remove(); mapRef.current = null; };
  }, []);

  // Update marker + fly-to whenever the pin moves
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !pin) return;
    if (markerRef.current) markerRef.current.remove();
    // Signify brand green so the pin matches the rest of the UI.
    markerRef.current = new maplibregl.Marker({ color: "#00b56b" })
      .setLngLat([pin.lon, pin.lat])
      .addTo(map);
    map.flyTo({ center: [pin.lon, pin.lat], zoom: 17, speed: 1.2 });
  }, [pin]);

  // ── Load existing project on edit mode ───────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/projects/${editId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const p = await res.json();
        if (cancelled) return;
        setForm({
          project_name: p.project_name || "",
          priority: p.priority || "normal",
          project_number: p.project_number || "",
          country: p.country || "",
          project_address: p.project_address || "",
          project_town: p.project_town || "",
          project_type: p.project_type || "",
          auto_luminaire_sweep:
            p.auto_luminaire_sweep === undefined ? true : !!p.auto_luminaire_sweep,
        });
        // 2026-05-21: if the project already has persisted coords
        // (migration 0009), use those directly — no network call. For
        // legacy projects without coords, do a one-shot Nominatim
        // lookup from the address as a fallback and let the user save
        // to backfill `project_lon/lat` for next time.
        if (p.project_lon != null && p.project_lat != null) {
          setPin({ lon: p.project_lon, lat: p.project_lat });
        } else if (p.project_address || p.project_town) {
          const q = [p.project_address, p.project_town].filter(Boolean).join(" ");
          const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`;
          try {
            const r = await fetch(url);
            const arr = await r.json();
            if (arr && arr[0]) setPin({ lon: parseFloat(arr[0].lon), lat: parseFloat(arr[0].lat) });
          } catch (_) { /* ignore */ }
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, editId]);

  // ── Address autocomplete (debounce + Nominatim, German results first) ─
  const searchAddress = (query) => {
    if (searchTimer) clearTimeout(searchTimer);
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=de,at,ch&limit=6`;
      try {
        const r = await fetch(url);
        const arr = await r.json();
        setSuggestions(Array.isArray(arr) ? arr : []);
      } catch (e) {
        console.error("Nominatim", e);
        setSuggestions([]);
      }
    }, 300);
    setSearchTimer(t);
  };

  const pickSuggestion = (item) => {
    const addr = item.address || {};
    setForm((prev) => ({
      ...prev,
      project_address: [addr.road, addr.house_number].filter(Boolean).join(" ")
        || item.display_name.split(",")[0],
      project_town: addr.city || addr.town || addr.village || addr.municipality || prev.project_town,
      country: addr.country || prev.country,
    }));
    setAddressQuery(item.display_name);
    setSuggestions([]);
    setPin({ lon: parseFloat(item.lon), lat: parseFloat(item.lat) });
  };

  // ── Save ─────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.project_name.trim()) {
      setError("Projektname ist Pflicht.");
      return;
    }
    setSaving(true);
    try {
      const url = isEdit
        ? `${API_BASE}/projects/${editId}`
        : `${API_BASE}/projects`;
      const method = isEdit ? "PATCH" : "POST";
      // 2026-05-21: include the picked map pin (lon/lat) in the
      // payload so the backend persists it on the Project. SketchPage
      // then reopens the map exactly there without a Nominatim
      // re-lookup. Falls back to undefined when the user didn't pick
      // an address — backend treats null as "no change" on PATCH.
      const body = {
        ...form,
        project_lon: pin ? pin.lon : null,
        project_lat: pin ? pin.lat : null,
      };
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const id = isEdit ? Number(editId) : data.id;
      // Pass the picked address pin + current map zoom through Router
      // state so the SketchPage map opens already centered on the
      // sportplatz (instead of the default Germany centroid). Read
      // `mapRef.current.getZoom()` if available so a user-tweaked zoom
      // level survives the transition.
      let zoom = 17;
      try { if (mapRef.current?.getZoom) zoom = mapRef.current.getZoom(); } catch (_) { /* ignore */ }
      const navState = pin
        ? { state: { initialMapView: { lon: pin.lon, lat: pin.lat, zoom } } }
        : undefined;
      // After create → straight into sketching; after edit → back to detail.
      navigate(isEdit ? `/projects/${id}` : `/projects/${id}/sketch`, navState);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Page-level grid only — every field/button/banner pulls its look
  // from the design-system utility classes in App.css. Tightened
  // 2026-05-21 per Philip's "viel zu viel white space" feedback.
  const pageStyle = {
    display: "grid",
    gridTemplateColumns: "minmax(360px, 1fr) 1.4fr",
    gap: 14,
    padding: "16px 20px",
    minHeight: "100%",
  };
  const mapWrapStyle = {
    position: "sticky", top: 16,
    minHeight: 480, height: "calc(100vh - 110px)",
    borderRadius: "var(--radius-lg)", overflow: "hidden",
    border: "1px solid var(--border-default)",
    boxShadow: "var(--shadow-sm)",
  };

  return (
    <div style={pageStyle}>
      <form onSubmit={handleSubmit}>
        <div
          className="section-title"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "var(--space-5)",
          }}
        >
          <div>
            <h2>{isEdit ? "Projekt bearbeiten" : "Neues Projekt"}</h2>
            <div className="subtitle">
              {isEdit
                ? "Metadaten anpassen — Skizze bleibt erhalten"
                : "Stammdaten und Sportplatz-Adresse"}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => navigate("/projects")}
          >← Zur Liste</button>
        </div>

        <div className="report-card">
          <div className="report-card-header">
            <h3>Projektdaten</h3>
            <div className="subtitle">Name, Nummer, Adresse, Sportart</div>
          </div>
          <div className="report-card-body">

            <div className="field">
              <label className="field-label">Projektname *</label>
              <input
                className="field-input"
                value={form.project_name}
                onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                placeholder="z.B. Sportplatz Lüdingworth"
                required
              />
            </div>

            <div className="field">
              <label className="field-label">Projektnummer</label>
              <input
                className="field-input"
                value={form.project_number}
                onChange={(e) => setForm({ ...form, project_number: e.target.value })}
                placeholder="z.B. SST-12330"
              />
            </div>

            <div className="field">
              <label className="field-label">Adresse</label>
              <div style={{ position: "relative" }}>
                <input
                  className="field-input"
                  value={addressQuery || form.project_address}
                  onChange={(e) => { setAddressQuery(e.target.value); searchAddress(e.target.value); }}
                  placeholder={'z.B. 30161 oder „Sportplatz Cuxhaven"'}
                />
                {suggestions.length > 0 && (
                  <div className="suggest-menu">
                    {suggestions.map((s, i) => (
                      <div
                        key={i}
                        className="suggest-item"
                        onClick={() => pickSuggestion(s)}
                      >
                        {s.display_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="field-help">
                Autocomplete via Nominatim — PLZ alleine reicht.
              </div>
            </div>

            <div className="field">
              <label className="field-label">Stadt</label>
              <input
                className="field-input"
                value={form.project_town}
                onChange={(e) => setForm({ ...form, project_town: e.target.value })}
                placeholder="wird beim Auswählen der Adresse automatisch befüllt"
              />
            </div>

            {/* Land is filled in automatically from Nominatim when an
                address suggestion is picked. Shown read-only so the
                user can see what got captured; not editable to avoid
                drift. */}
            <div className="field">
              <label className="field-label">Land</label>
              <input
                className="field-input"
                value={form.country || ""}
                readOnly
                tabIndex={-1}
                placeholder="wird beim Auswählen der Adresse automatisch befüllt"
              />
            </div>

            <div className="field">
              <label className="field-label">Projektart</label>
              <select
                className="field-select"
                value={form.project_type}
                onChange={(e) => setForm({ ...form, project_type: e.target.value })}
              >
                {PROJECT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* The "Automatisch alternative Leuchten prüfen" toggle
                lives on the sketching step (ProjectDetailsForm in the
                wizard), not here — it only matters at calc time. */}

            {error && (
              <div className="banner banner-fail" style={{ marginTop: "var(--space-4)" }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={saving}
              style={{ marginTop: "var(--space-5)", width: "100%" }}
            >
              {saving ? "Speichere…" : (isEdit ? "Änderungen speichern" : "Projekt anlegen & weiter zum Sportplatz")}
            </button>
          </div>
        </div>
      </form>

      <div style={mapWrapStyle}>
        <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />
      </div>
    </div>
  );
};

export default CreateProjectPage;
